import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
	searchByPersonId,
	postUser,
	getUserRolesByUserIdAndAppId,
	getUsersByOrgHierarchy,
	getTreeByDomain,
	getAppRolesByAppId,
	postUserApp,
	postUserRole,
	getDelegateUserByUserId,
	getDelegateUserByForUserId,
	postDelegateUser,
} from "api";
import _ from "lodash";
import { selectGlobal } from "reducers/global";

export const validatePerson = createAsyncThunk(
	"admin/validatePerson",
	async (person, { dispatch }) => {
		const { userId, firstName, lastName, employeeId, jobTitle, email } =
			person;
		let errors = [];
		if (
			!userId ||
			!firstName ||
			!lastName ||
			!employeeId ||
			!jobTitle ||
			!email
		)
			errors.push(
				"Please fill out all mandatory fields marked with a red asterisk"
			);
		dispatch(validationErrorsEdited(errors));
		return errors.length;
	}
);

export const fetchPersonById = createAsyncThunk(
	"admin/fetchPersonById",
	async (personId, { rejectWithValue }) => {
		try {
			const data = await searchByPersonId({ personId });
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchTeamUserRoles = createAsyncThunk(
	"admin/fetchTeamUserRoles",
	async ({ organisationHierarchyId }, { getState, rejectWithValue }) => {
		try {
			const { appId } = selectGlobal(getState());
			const response = await getUsersByOrgHierarchy({
				organisationHierarchyId,
				domain: "Test",
				deepSearch: false,
				includeNewUser: false,
				appId,
			});
			return response;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchUserRoles = createAsyncThunk(
	"admin/fetchUserRoles",
	async ({ userId }, { getState, rejectWithValue }) => {
		try {
			const { appId } = selectGlobal(getState());
			const data = await getUserRolesByUserIdAndAppId({
				userId,
				appId,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postPerson = createAsyncThunk(
	"admin/postPerson",
	async (data, { getState, rejectWithValue }) => {
		const { appId } = selectGlobal(getState());
		try {
			const response = await postUser(data);
			await postUserApp({ userId: data.id, appId });
			return response;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchTree = createAsyncThunk(
	"admin/fetchTree",
	async (_, { getState, rejectWithValue }) => {
		try {
			const { domain } = selectGlobal(getState());
			const data = await getTreeByDomain({
				domain,
				allowAllSelect: true,
			});
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchRoles = createAsyncThunk(
	"admin/fetchRoles",
	async (_, { getState, rejectWithValue }) => {
		try {
			const { appId } = selectGlobal(getState());
			const data = await getAppRolesByAppId({ appId });
			return data;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postTeamMembership = createAsyncThunk(
	"admin/postTeamMembership",
	async (
		{
			id,
			organisationHierarchyId,
			userId,
			roleId,
			priority,
			isDefault,
			startDate,
			endDate,
			isDeleted,
		},
		{ getState, rejectWithValue, dispatch }
	) => {
		try {
			const { appId } = selectGlobal(getState());
			await postUserApp({ userId, appId });
			await postUserRole({
				id,
				organisationHierarchyId,
				userId,
				roleId,
				priority,
				isDefault,
				startDate,
				endDate,
				isDeleted,
			});
			await dispatch(fetchUserRoles({ userId }));
			await dispatch(fetchTeamUserRoles({ organisationHierarchyId }));
			return;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchUserDelegates = createAsyncThunk(
	"admin/fetchUserDelegates",
	async ({ userId }, { rejectWithValue }) => {
		try {
			const myDelegateUsers = await getDelegateUserByUserId({
				userId,
			});
			const usersDelegatedForMe = await getDelegateUserByForUserId({
				forUserId: userId,
			});
			return [...myDelegateUsers, ...usersDelegatedForMe];
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postUserDelegate = createAsyncThunk(
	"admin/postUserDelegate",
	async (
		{
			thisPersonId,
			id,
			userId,
			delegatedForUser,
			type,
			startDate,
			endDate,
			ccEmail,
			isInactive = false,
		},
		{ rejectWithValue, dispatch }
	) => {
		try {
			await postDelegateUser([
				{
					id,
					userId,
					delegatedForUser,
					type,
					startDate,
					endDate,
					ccEmail,
					isInactive,
				},
			]);
			await dispatch(fetchUserDelegates({ userId: thisPersonId }));
			return;
		} catch (error) {
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

const initialState = {
	person: {},
	userRoles: [],
	userDelegates: [],
	teamUserRoles: [],
	roles: [],
	teams: [],
	leaveApplications: [],
	status: "idle",
	error: null,
	validationErrors: [],
};

const adminSlice = createSlice({
	name: "admin",
	initialState,
	reducers: {
		validationErrorsEdited(state, action) {
			state.validationErrors = action.payload;
		},
		errorEdited(state, action) {
			state.error = action.payload;
		},
		personEdited(state, action) {
			state.person = action.payload;
		},
		userRolesReceived(state, action) {
			state.userRoles = action.payload;
		},
		teamEdited(state, action) {
			state.team = action.payload;
		},
		teamUserRolesReceived(state, action) {
			state.teamUserRoles = action.payload;
		},
		statusEdited(state, action) {
			state.status = action.payload;
		},
	},
	extraReducers: {
		RESET_ADMIN: (state, action) => {
			state.person = {};
			state.userRoles = [];
			state.teamUserRoles = [];
			state.leaveApplications = [];
			state.error = null;
			state.validationErrors = [];
		},
		[postPerson.pending]: (state, action) => {
			state.status = "loading";
		},
		[postPerson.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[postPerson.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postTeamMembership.pending]: (state, action) => {
			state.status = "loading";
		},
		[postTeamMembership.fulfilled]: (state, action) => {
			state.status = "success";
		},
		[postTeamMembership.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchPersonById.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchPersonById.fulfilled]: (state, action) => {
			state.status = "idle";
			state.person = action.payload; //set person data
		},
		[fetchPersonById.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.person = {};
		},
		[fetchTree.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTree.fulfilled]: (state, action) => {
			state.status = "idle";
			const sortedTeams = _.orderBy(action.payload, ["name"], ["asc"]);
			state.teams = sortedTeams;
		},
		[fetchTree.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.teams = [];
		},
		[fetchRoles.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchRoles.fulfilled]: (state, action) => {
			state.status = "idle";
			const sortedRoles = _.orderBy(
				action.payload,
				["roleName"],
				["asc"]
			);
			state.roles = sortedRoles;
		},
		[fetchRoles.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.roles = [];
		},
		[fetchUserRoles.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchUserRoles.fulfilled]: (state, action) => {
			state.status = "idle";
			const sortedUserRoles = _.orderBy(
				action.payload,
				["organisationHierarchyName", "roleName"],
				["asc", "asc"]
			);
			state.userRoles = sortedUserRoles;
		},
		[fetchUserRoles.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.userRoles = {};
		},
		[fetchTeamUserRoles.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchTeamUserRoles.fulfilled]: (state, action) => {
			state.status = "idle";
			const sorted = _.orderBy(
				action.payload,
				["fullName", "roleName"],
				["asc", "asc"]
			);
			state.teamUserRoles = sorted;
		},
		[fetchTeamUserRoles.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.teamUserRoles = {};
		},
		[fetchUserDelegates.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchUserDelegates.fulfilled]: (state, action) => {
			state.status = "idle";
			state.userDelegates = action.payload;
		},
		[fetchUserDelegates.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
			state.userDelegates = [];
		},
	},
});

export const {
	personEdited,
	userRolesReceived,
	teamUserRolesReceived,
	validationErrorsEdited,
	errorEdited,
	statusEdited,
} = adminSlice.actions;

export const selectAdminPerson = (state) => state.capex.admin.person;
export const selectAdminUserRoles = (state) => state.capex.admin.userRoles;
export const selectAdminUserDelegates = (state) =>
	state.capex.admin.userDelegates;
export const selectAdminTeamUserRoles = (state) =>
	state.capex.admin.teamUserRoles;
export const selectAdminTree = (state) => state.capex.admin.teams;
export const selectAdminRoles = (state) => state.capex.admin.roles;
export const selectAdminLeaveApplications = (state) =>
	state.capex.admin.leaveApplications;

export const selectAdminStatus = (state) => state.capex.admin.status;
export const selectAdminValidationErrors = (state) =>
	state.capex.admin.validationErrors;
export const selectAdminError = (state) => state.capex.admin.error;

export default adminSlice.reducer;
